import classnames from 'classnames';
import type React from 'react';
import {
  Children,
  cloneElement,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
  type NamedExoticComponent,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import { ArrowDown } from '@/core/tamagoshi/icons/ArrowDown';

import { FormFieldMessage } from '../FormFieldMessage/FormFieldMessage';
import { Underlay } from '../Underlay/Underlay';
import { getComputedScrollTop } from './utils/getComputedScrollTop';

import styles from './scss/SelectInput.module.scss';

export const STATUS_ERROR = 'error';
export const STATUS_NORMAL = 'normal';
export const STATUS_SUCCESS = 'success';

export type SelectStatus =
  | typeof STATUS_NORMAL
  | typeof STATUS_SUCCESS
  | typeof STATUS_ERROR;

const getChildOfType = (children: ReactElement, type: string) =>
  Children.map(
    children,
    (child) =>
      ((child as ReactElement)?.type as NamedExoticComponent)?.displayName ===
        type && child,
  );

const getActiveChildIds = (children: ReactElement, type: string) => {
  const activeIds: number[] = [];
  Children.map(children, (child, idx) => {
    if (
      ((child as ReactElement)?.type as NamedExoticComponent)?.displayName ===
        type &&
      !(child as ReactElement)?.props.isDisabled
    ) {
      activeIds.push(idx);
    }
  });

  return activeIds;
};

const getActiveIdsByOptionValues = (children: ReactElement, type: string) => {
  const activeIdsByOptionValues: Record<string, number> = {};
  Children.map(children, (child, idx) => {
    if (
      ((child as ReactElement)?.type as NamedExoticComponent)?.displayName ===
        type &&
      !child.props.isDisabled
    ) {
      const { value } = child.props;
      activeIdsByOptionValues[value] = idx;
    }
  });

  return activeIdsByOptionValues;
};

/**
 * SelectInput component.
 */
export interface SelectInputProps extends PropsWithChildren {
  id?: string;
  name?: string;
  /** To override the style for input */
  className?: string;
  /** A place holder text */
  label?: string;
  /** one of the option value to be selected by default, if value does not matches with one of the options, there will not be a default selected option */
  value?: string;
  /**  Test id for end to end test */
  testId?: string;
  /** To show error or success or normal state of select */
  status?: SelectStatus;
  /** Message to appear at the bottom */
  message?: string;
  /** to enable disable wordwrap else appears in single line, hidden with ellipse */
  isWordWrap?: boolean;
  /** to enable disable the select */
  isDisabled?: boolean;
  /** To override options Selected style */
  classOptionSelect?: string;
  /** To override options focused style */
  classOptionFocus?: string;
  /** To override options normal style */
  classOptionNormal?: string;
  /** To override options disabled style */
  classOptionDisabled?: string;
  /** To style the drop down list */
  classList?: string;
  /** callback on change of selection, called with event and value arguments */
  onChange?: (event: KeyboardEvent | MouseEvent, value: string) => any | void;
}

export const SelectInput: FunctionComponent<SelectInputProps> = ({
  id,
  name,
  className,
  status = STATUS_NORMAL,
  message = '',
  label = '',
  value = '',
  isWordWrap = false,
  isDisabled = false,
  classOptionSelect = '',
  classOptionFocus = '',
  classOptionNormal = '',
  classOptionDisabled = '',
  classList = '',
  testId,
  onChange,
  children,
}) => {
  const [optionsVisible, setOptionsVisible] = useState(false);
  const selectListRef = useRef<HTMLUListElement | null>(null);
  const allOptions = getChildOfType(children as ReactElement, 'Option');
  const activeOptionIds: number[] = getActiveChildIds(
    children as ReactElement,
    'Option',
  );
  const activeIdsByOptionValues = getActiveIdsByOptionValues(
    children as ReactElement,
    'Option',
  );

  const currentWordWrap = isWordWrap ? styles.wordWrap : styles.noWordWrap;

  const initialOptions = activeOptionIds.filter(
    (idx) => allOptions[idx].props.value === value,
  );
  const initialIndex = activeOptionIds.indexOf(initialOptions[0]);
  const [selectedOptionId, setSelectedOptionId] = useState(initialIndex);
  const [focusedIdx, setFocusedIndex] = useState(initialIndex);

  const getOptionByActiveId = (index: number) =>
    allOptions[activeOptionIds[index]];

  const getActiveIdByOptionValue = (optionValue: string) =>
    activeIdsByOptionValues?.[optionValue];

  const toLocalIdx = (activeIndex: number) => activeOptionIds[activeIndex];

  const handleHide = () => {
    setOptionsVisible(false);
    setFocusedIndex(selectedOptionId);
  };

  const handleOptionSelection = (
    event: KeyboardEvent | MouseEvent,
    focusedId: number,
  ) => {
    event.preventDefault();
    if (onChange) {
      onChange(event, getOptionByActiveId(focusedId)?.props?.value);
    }
    setFocusedIndex(focusedId);
    setSelectedOptionId(focusedId);
    setOptionsVisible(false);
  };

  const navigate = (event: KeyboardEvent) => {
    let newFocusIdx;
    if (!activeOptionIds.length) return;
    const { code } = event;
    switch (code) {
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        if (optionsVisible && selectListRef.current) {
          newFocusIdx = focusedIdx
            ? focusedIdx - 1
            : activeOptionIds.length - 1;
          setFocusedIndex(newFocusIdx);
          selectListRef.current.scrollTop = getComputedScrollTop(
            selectListRef.current.childNodes,
            toLocalIdx(newFocusIdx),
          );
        } else {
          setOptionsVisible(true);
        }
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        if (optionsVisible && selectListRef.current) {
          newFocusIdx =
            focusedIdx < activeOptionIds.length - 1 ? focusedIdx + 1 : 0;
          setFocusedIndex(newFocusIdx);
          selectListRef.current.scrollTop = getComputedScrollTop(
            selectListRef.current.childNodes,
            toLocalIdx(newFocusIdx),
          );
        } else {
          setOptionsVisible(true);
        }
        break;
      case 'Enter':
        event.preventDefault();
        handleOptionSelection(event, focusedIdx);
        break;
      case 'Escape':
        event.preventDefault();
        handleHide();
        break;
      default:
    }
  };

  const handleOptionFocus = (event: FocusEvent, index: number) => {
    event.preventDefault();
    setFocusedIndex(activeOptionIds.indexOf(index));
  };

  const getContainerStatusStyle = (selectStatus: SelectStatus) => {
    switch (selectStatus) {
      case STATUS_ERROR:
        return styles.selectContainerError;
      case STATUS_SUCCESS:
        return styles.selectContainerSuccess;
      default:
        return null;
    }
  };

  const rootClassNames = classnames([styles.selectInputContainer, className]);
  const buttonClassNames = classnames(
    styles.selectContainer,
    {
      [styles.selectContainerLabel]: label && selectedOptionId !== -1,
      [styles.selectContainerDisabled]: isDisabled,
    },
    getContainerStatusStyle(status),
  );

  useEffect(() => {
    let listenerActivated = false;
    if (optionsVisible) {
      document.addEventListener('keydown', navigate);
      listenerActivated = true;
    }
    return function cleanup() {
      if (listenerActivated) {
        document.removeEventListener('keydown', navigate);
      }
    };
  });

  useEffect(() => {
    if (optionsVisible && selectListRef.current) {
      selectListRef.current.scrollTop = getComputedScrollTop(
        selectListRef.current.childNodes,
        toLocalIdx(focusedIdx),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, optionsVisible]);

  useEffect(() => {
    if (value) {
      const selectedId = getActiveIdByOptionValue(value);
      if (selectedId !== undefined) {
        setSelectedOptionId(selectedId);
      }
    } else {
      setSelectedOptionId(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className={rootClassNames}>
      <button
        id={id}
        name={name}
        className={buttonClassNames}
        type="button"
        onClick={() => {
          if (optionsVisible) {
            handleHide();
          } else {
            setOptionsVisible(true);
          }
        }}
        data-testid={testId}
        disabled={isDisabled}
      >
        <span className={classnames([currentWordWrap])}>
          <div
            className={
              selectedOptionId < 0
                ? styles.selectLabelNormal
                : styles.selectLabelSuperScript
            }
          >
            {label}
          </div>
          {selectedOptionId >= 0 && selectedOptionId < activeOptionIds.length
            ? cloneElement(getOptionByActiveId(selectedOptionId), {
                className: styles.aoptionPicked,
                isDisabled: false,
              })
            : null}
        </span>
        <ArrowDown
          className={classnames([
            styles.selectArrow,
            optionsVisible ? styles.selectArrowOpen : styles.selectArrowClose,
          ])}
        />
      </button>
      {!isDisabled && optionsVisible && !!allOptions?.length && (
        <>
          <ul
            className={classnames([styles.selectionList, classList])}
            ref={selectListRef}
            data-testid={testId ? `${testId}-options` : undefined}
          >
            {allOptions.map((option, idx) =>
              cloneElement(option, {
                classDisabled: classOptionDisabled,
                classFocus: classOptionFocus,
                className: classOptionNormal,
                classSelected: classOptionSelect,
                isFocused: idx === activeOptionIds[focusedIdx],
                isSelected: idx === activeOptionIds[selectedOptionId],
                isWordWrap,
                onClick: (evt: React.MouseEvent) => {
                  if (!option.props.isDisabled) {
                    const activeOptionsIdx = activeOptionIds.indexOf(idx);
                    handleOptionSelection(evt.nativeEvent, activeOptionsIdx);
                  }
                },
                onMouseOver: (event: FocusEvent) => {
                  if (!option.props.isDisabled) {
                    handleOptionFocus(event, idx);
                  }
                },
                role: 'option',
              }),
            )}
          </ul>
        </>
      )}
      {message && (
        <FormFieldMessage
          message={message}
          testId={testId}
          messageType={status === STATUS_ERROR ? 'danger' : 'none'}
        />
      )}
      {!isDisabled && optionsVisible && (
        <Underlay
          underlayVisible={optionsVisible}
          clickUnderlay={handleHide}
          zIndex={9}
        />
      )}
    </div>
  );
};

SelectInput.displayName = 'SelectInput';
